import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pageHeroShellSx,
  pageContentCardSx,
  pageFieldSx,
  pagePrimaryButtonSx,
  pageSectionTitleSx,
  pageMobileCardSx,
  pageKpiChipSx,
} from "../../styles/pageModernStyles";
import { getAllUsers } from "../../api/User/userApi";
import {
  deleteVisitCard,
  deleteVisitCardCompany,
  getIssuedVisitCards,
  getMyVisitCards,
  getVisitCardCompanies,
  getVisitCardsMeta,
  saveVisitCard,
  saveVisitCardCompany,
  visitCardAssetUrl,
  getVisitCardWechatQr,
  getVisitCardWechatQrImageBlob,
} from "../../api/User/visitCardsApi";
import {
  slugifyVisitCard,
  peopleForVisitCards,
} from "../../utils/visitCardAccess";
import { normalizeVisitCardPhoto } from "../../utils/visitCardPhoto";
import {
  VISIT_CARD_LOGOS,
  resolvedVisitCardLogoKey,
  visitCardLogo,
} from "../../constants/visitCardLogos";

function emptyAddress(overrides = {}) {
  return {
    label: "",
    street: "",
    city: "",
    zip: "",
    region: "",
    country: "España",
    mapsUrl: "",
    responsible: "",
    ...overrides,
  };
}

function emptyCompany() {
  return {
    name: "",
    legalName: "",
    slug: "",
    website: "",
    brandColor: "#00c2a3",
    logoKey: "stage",
    phone: "",
    email: "",
    active: true,
    sortOrder: 0,
    addresses: [emptyAddress()],
  };
}

function companyCities(company) {
  const seen = new Set();
  return (company?.addresses || [])
    .map((addr) => String(addr.city || addr.label || "").trim())
    .filter((city) => {
      const key = city.toLowerCase();
      if (!city || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function companyAddressesForForm(company) {
  return (company?.addresses || []).map((addr) => ({
    ...addr,
    responsible: addr.responsible || addr.Responsible || "",
  }));
}

function emptyCard() {
  return {
    userId: "",
    companyId: "",
    userSlug: "",
    cardSlug: "",
    fullName: "",
    roleTitle: "",
    email: "",
    phone: "",
    mobile: "",
    whatsapp: "",
    wechat: "",
    linkedin: "",
    website: "",
    bio: "",
    active: true,
    photoBase64: "",
    photoMime: "image/jpeg",
    clearPhoto: false,
    wechatChinaPayload: "",
    wechatChinaImageBase64: "",
    wechatChinaImageMime: "image/jpeg",
    wechatChinaImagePreview: "",
    wechatChinaHasImage: false,
    clearWechatChina: false,
    companyAddresses: [],
  };
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function patchAddress(form, idx, patch) {
  const addresses = [...(form.addresses || [])];
  addresses[idx] = { ...addresses[idx], ...patch };
  return { ...form, addresses };
}

function nameInitials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function localeNameCompare(a = "", b = "") {
  return String(a)
    .trim()
    .localeCompare(String(b).trim(), "es", { sensitivity: "base" });
}

function groupIssuedByPerson(cards = []) {
  const groups = [];
  const index = new Map();
  for (const card of cards) {
    const name = String(card.fullName || "Sin nombre").trim();
    if (!index.has(name)) {
      index.set(name, groups.length);
      groups.push({ name, items: [] });
    }
    groups[index.get(name)].items.push(card);
  }
  for (const group of groups) {
    group.items.sort((a, b) =>
      localeNameCompare(
        a.company?.name || a.cardSlug || "",
        b.company?.name || b.cardSlug || "",
      ),
    );
  }
  groups.sort((a, b) => localeNameCompare(a.name, b.name));
  return groups;
}

function LogoBadge({ company, size = 44 }) {
  const logo = visitCardLogo(company?.logoKey, company);
  const accent = company?.brandColor || "#00c2a3";
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 2,
        p: 0.6,
        display: "grid",
        placeItems: "center",
        bgcolor: logo.lightBg ? "#fff" : alpha(accent, 0.12),
        border: "1px solid",
        borderColor: alpha(accent, 0.28),
      }}
    >
      <Box
        component="img"
        src={logo.src}
        alt=""
        sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
      />
    </Box>
  );
}

function listActionSx(theme, { filled = false, accent } = {}) {
  const color = accent || theme.palette.primary.main;
  return {
    textTransform: "none",
    fontWeight: 800,
    borderRadius: 2,
    px: 1.2,
    minHeight: 32,
    ...(filled
      ? {
          bgcolor: color,
          color: "#fff",
          "&:hover": { bgcolor: color, filter: "brightness(0.95)" },
        }
      : {
          borderColor: alpha(color, 0.4),
          color,
          "&:hover": { borderColor: color, bgcolor: alpha(color, 0.08) },
        }),
  };
}

function brandChipSx(accent) {
  return {
    height: 22,
    fontWeight: 700,
    fontSize: "0.68rem",
    bgcolor: alpha(accent, 0.14),
    color: accent,
    border: `1px solid ${alpha(accent, 0.28)}`,
  };
}

function brandCardSx(theme, accent) {
  const color = accent || theme.palette.primary.main;
  return {
    ...pageMobileCardSx(theme, { accentColor: color, active: true }),
    background: `linear-gradient(145deg, ${alpha(color, 0.18)} 0%, ${alpha(
      theme.palette.background.paper,
      0.98,
    )} 58%)`,
    borderColor: alpha(color, 0.34),
    boxShadow: `0 8px 24px ${alpha(color, 0.12)}`,
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 5,
      bgcolor: color,
    },
  };
}

const VisitCardsAdminPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { contentMargin } = useContext(SidebarContext);
  const [tab, setTab] = useState(0);
  const [meta, setMeta] = useState({ canManage: false });
  const [mine, setMine] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [companyForm, setCompanyForm] = useState(null);
  const [cardForm, setCardForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const people = useMemo(() => peopleForVisitCards(users), [users]);
  const issuedGroups = useMemo(() => groupIssuedByPerson(cards), [cards]);
  const companiesSorted = useMemo(
    () =>
      [...companies].sort((a, b) =>
        localeNameCompare(a.name || a.slug || "", b.name || b.slug || ""),
      ),
    [companies],
  );

  const load = useCallback(async () => {
    try {
      const info = await getVisitCardsMeta();
      setMeta(info);
      try {
        const mineCards = await getMyVisitCards();
        setMine(Array.isArray(mineCards) ? mineCards : []);
      } catch (err) {
        setMine([]);
        setError(
          err?.response?.data?.error || "No se pudieron cargar las tarjetas",
        );
      }
      if (info.canManage) {
        const [cos, issued, people] = await Promise.all([
          getVisitCardCompanies(),
          getIssuedVisitCards(),
          getAllUsers(),
        ]);
        setCompanies(cos);
        setCards(issued);
        setUsers(Array.isArray(people) ? people : []);
      }
    } catch (err) {
      setError(
        err?.response?.data?.error || "No se pudieron cargar las tarjetas",
      );
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const hydrateWechatChina = useCallback(async (userSlug, baseForm) => {
    const slug = String(userSlug || "").trim();
    if (!slug) {
      return {
        ...baseForm,
        wechatChinaPayload: "",
        wechatChinaImagePreview: "",
        wechatChinaHasImage: false,
        wechatChinaImageBase64: "",
        clearWechatChina: false,
      };
    }
    try {
      const info = await getVisitCardWechatQr(slug);
      let preview = "";
      if (info?.hasImage) {
        const blob = await getVisitCardWechatQrImageBlob(slug);
        preview = URL.createObjectURL(blob);
      }
      return {
        ...baseForm,
        wechatChinaPayload: info?.payload || baseForm.wechatChinaPayload || "",
        wechatChinaHasImage: Boolean(info?.hasImage),
        wechatChinaImagePreview: preview,
        wechatChinaImageBase64: "",
        clearWechatChina: false,
      };
    } catch {
      return {
        ...baseForm,
        wechatChinaPayload: baseForm.wechatChinaPayload || "",
        wechatChinaHasImage: Boolean(baseForm.wechatChinaHasImage),
        wechatChinaImagePreview: "",
        wechatChinaImageBase64: "",
        clearWechatChina: false,
      };
    }
  }, []);

  const openCardForm = useCallback(
    async (draft) => {
      const next = await hydrateWechatChina(draft.userSlug, draft);
      setCardForm(next);
    },
    [hydrateWechatChina],
  );

  const openNewCard = async () => {
    const draft = emptyCard();
    if (people[0]) {
      draft.userId = people[0].id;
      draft.userSlug = slugifyVisitCard(people[0].username);
      draft.fullName = people[0].username;
      draft.email = people[0].email || people[0].EMAIL || "";
    }
    if (companies[0]) {
      draft.companyId = companies[0].id;
      draft.cardSlug = companies[0].slug;
      draft.companyAddresses = companyAddressesForForm(companies[0]);
    }
    await openCardForm(draft);
  };

  const persistCompany = async () => {
    setSaving(true);
    try {
      await saveVisitCardCompany(
        {
          name: companyForm.name,
          legalName: companyForm.legalName || companyForm.name,
          slug: companyForm.slug,
          website: companyForm.website,
          brandColor: companyForm.brandColor,
          logoKey: resolvedVisitCardLogoKey(companyForm.logoKey, companyForm),
          phone: companyForm.phone,
          email: companyForm.email,
          active: companyForm.active,
          sortOrder: companyForm.sortOrder,
          addresses: companyForm.addresses || [],
        },
        companyForm.id,
      );
      setCompanyForm(null);
      setOk("Empresa guardada");
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo guardar la empresa");
    } finally {
      setSaving(false);
    }
  };

  const persistCard = async () => {
    setSaving(true);
    try {
      const payload = {
        userId: Number(cardForm.userId),
        companyId: Number(cardForm.companyId),
        userSlug: cardForm.userSlug,
        cardSlug: cardForm.cardSlug,
        fullName: cardForm.fullName,
        roleTitle: cardForm.roleTitle,
        email: cardForm.email,
        phone: cardForm.phone,
        mobile: cardForm.mobile,
        whatsapp: cardForm.whatsapp,
        wechat: cardForm.wechat,
        linkedin: cardForm.linkedin,
        website: cardForm.website,
        bio: cardForm.bio,
        active: cardForm.active,
        clearPhoto: cardForm.clearPhoto,
        wechatChinaPayload: cardForm.wechatChinaPayload || "",
        clearWechatChina: Boolean(cardForm.clearWechatChina),
      };
      if (cardForm.photoBase64) {
        payload.photoBase64 = cardForm.photoBase64;
        payload.photoMime = cardForm.photoMime || "image/jpeg";
      }
      if (cardForm.wechatChinaImageBase64) {
        payload.wechatChinaImageBase64 = cardForm.wechatChinaImageBase64;
        payload.wechatChinaImageMime =
          cardForm.wechatChinaImageMime || "image/jpeg";
      }
      await saveVisitCard(payload, cardForm.id);
      const company = companies.find(
        (c) => Number(c.id) === Number(cardForm.companyId),
      );
      if (company && (cardForm.companyAddresses || []).length) {
        await saveVisitCardCompany(
          {
            name: company.name,
            legalName: company.legalName || company.name,
            slug: company.slug,
            website: company.website,
            brandColor: company.brandColor,
            logoKey: resolvedVisitCardLogoKey(company.logoKey, company),
            phone: company.phone,
            email: company.email,
            active: company.active !== false,
            sortOrder: company.sortOrder,
            addresses: cardForm.companyAddresses,
          },
          company.id,
        );
      }
      setCardForm(null);
      setOk("Tarjeta emitida");
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo guardar la tarjeta");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ ...contentMargin, p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ ...pageHeroShellSx(theme), mb: 2 }}>
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ md: "center" }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                  color: "primary.main",
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.28),
                }}
              >
                <ContactMailOutlinedIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: 1.1, lineHeight: 1.1 }}
                >
                  USER
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.15,
                    fontSize: { xs: "1.35rem", sm: "1.9rem" },
                  }}
                >
                  {t("visitCards.title")}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.4 }}>
                  {meta.canManage
                    ? t("visitCards.subtitleManage")
                    : t("visitCards.subtitleMine")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 1 }}
                >
                  <Chip
                    label={`${mine.length} ${mine.length === 1 ? "tarjeta" : "tarjetas"}`}
                    size="small"
                    sx={{
                      ...pageKpiChipSx,
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: "primary.main",
                    }}
                  />
                  {meta.canManage ? (
                    <>
                      <Chip
                        label={`${companies.length} empresas`}
                        size="small"
                        sx={{
                          ...pageKpiChipSx,
                          bgcolor: alpha("#1565c0", 0.12),
                          color: "#1565c0",
                        }}
                      />
                      <Chip
                        label={`${cards.length} emitidas`}
                        size="small"
                        sx={{
                          ...pageKpiChipSx,
                          bgcolor: alpha("#2e7d32", 0.12),
                          color: "#2e7d32",
                        }}
                      />
                    </>
                  ) : null}
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </Box>
      </Box>

      <Box sx={pageContentCardSx(theme)}>
        <Typography sx={pageSectionTitleSx}>Tus tarjetas</Typography>
        {error && !mine.length ? (
          <Alert severity="warning" sx={{ mt: 1.5, mb: 1 }}>
            {error}
          </Alert>
        ) : null}
        {mine.length ? (
          <Box
            sx={{
              mt: 1.5,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(280px, 1fr))",
              },
              gap: 1.5,
            }}
          >
            {mine.map((card) => {
              const accent =
                card.company?.brandColor || theme.palette.primary.main;
              return (
                <Box
                  key={card.id}
                  sx={{
                    ...pageMobileCardSx(theme, { accentColor: accent }),
                    p: 1.75,
                    pl: 2.15,
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: theme.palette.background.paper,
                  }}
                >
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <LogoBadge company={card.company} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography fontWeight={800} sx={{ lineHeight: 1.2 }}>
                        {card.company?.name}
                      </Typography>
                      {card.roleTitle ? (
                        <Typography variant="body2" color="text.secondary">
                          {card.roleTitle}
                        </Typography>
                      ) : null}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", fontWeight: 700 }}
                      >
                        /{card.cardSlug || card.company?.slug}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        flexShrink: 0,
                        borderRadius: 1.5,
                        bgcolor: "#fff",
                        p: 0.4,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        component="img"
                        src={visitCardAssetUrl(card.qrUrl)}
                        alt=""
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={0.8}
                    mt={1.6}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<OpenInNewOutlinedIcon />}
                      href={card.publicUrl}
                      target="_blank"
                      rel="noreferrer"
                      sx={listActionSx(theme, { filled: true })}
                    >
                      Abrir
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      href={visitCardAssetUrl(card.vcfUrl)}
                      sx={listActionSx(theme)}
                    >
                      Contacto
                    </Button>
                    {card.walletEnabled ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AccountBalanceWalletOutlinedIcon />}
                        href={visitCardAssetUrl(card.walletUrl)}
                        sx={listActionSx(theme)}
                      >
                        {t("visitCards.addToWallet")}
                      </Button>
                    ) : null}
                    {card.walletChinaEnabled ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AccountBalanceWalletOutlinedIcon />}
                        href={visitCardAssetUrl(card.walletChinaUrl)}
                        sx={listActionSx(theme)}
                      >
                        {t("visitCards.addToWalletChina")}
                      </Button>
                    ) : null}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<QrCode2OutlinedIcon />}
                      href={visitCardAssetUrl(card.qrUrl)}
                      download
                      sx={listActionSx(theme)}
                    >
                      QR
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        ) : error ? null : (
          <Typography color="text.secondary" sx={{ mt: 1.5 }}>
            {t("visitCards.emptyMine")}
          </Typography>
        )}
      </Box>

      {meta.canManage ? (
        <Box sx={{ ...pageContentCardSx(theme), mt: 2 }}>
          <Tabs
            value={tab}
            onChange={(_e, value) => setTab(value)}
            sx={{
              mb: 2,
              minHeight: 42,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 800,
                minHeight: 42,
                letterSpacing: 0.2,
              },
            }}
          >
            <Tab label={`Empresas y direcciones · ${companiesSorted.length}`} />
            <Tab label={`Tarjetas emitidas · ${cards.length}`} />
          </Tabs>

          {tab === 0 ? (
            <>
              <Button
                startIcon={<AddOutlinedIcon />}
                sx={pagePrimaryButtonSx(theme)}
                onClick={() => setCompanyForm(emptyCompany())}
              >
                Nueva empresa
              </Button>
              <Box
                sx={{
                  mt: 2,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(auto-fill, minmax(280px, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                {companiesSorted.map((co) => {
                  const accent = co.brandColor || "#00c2a3";
                  const cities = companyCities(co);
                  return (
                    <Box
                      key={co.id}
                      sx={{
                        ...brandCardSx(theme, accent),
                        p: 1.75,
                        pl: 2.1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 168,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.2}
                        alignItems="flex-start"
                      >
                        <LogoBadge company={co} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography fontWeight={800} sx={{ lineHeight: 1.2 }}>
                            {co.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 700 }}
                          >
                            /{co.slug}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={0.6}
                        mt={1.3}
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ flex: 1 }}
                      >
                        {cities.length ? (
                          cities.map((city) => (
                            <Chip
                              key={city}
                              size="small"
                              label={city}
                              sx={brandChipSx(accent)}
                            />
                          ))
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Sin ciudades
                          </Typography>
                        )}
                      </Stack>
                      <Button
                        size="small"
                        startIcon={<EditOutlinedIcon />}
                        sx={{
                          ...listActionSx(theme, { accent }),
                          alignSelf: "flex-start",
                          mt: 1.4,
                        }}
                        onClick={() =>
                          setCompanyForm({
                            ...emptyCompany(),
                            ...co,
                            addresses:
                              Array.isArray(co.addresses) && co.addresses.length
                                ? companyAddressesForForm(co)
                                : [emptyAddress()],
                          })
                        }
                      >
                        Editar
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            </>
          ) : (
            <>
              <Button
                startIcon={<AddOutlinedIcon />}
                sx={pagePrimaryButtonSx(theme)}
                onClick={openNewCard}
              >
                Emitir tarjeta
              </Button>
              <Stack spacing={2.5} mt={2}>
                {issuedGroups.map((group) => {
                  const groupAccent =
                    group.items[0]?.company?.brandColor ||
                    theme.palette.primary.main;
                  return (
                    <Box key={group.name}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1.2, px: 0.25 }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            display: "grid",
                            placeItems: "center",
                            fontWeight: 800,
                            fontSize: "0.68rem",
                            color: "#fff",
                            bgcolor: groupAccent,
                            flexShrink: 0,
                          }}
                        >
                          {nameInitials(group.name)}
                        </Box>
                        <Typography sx={{ fontWeight: 800 }}>
                          {group.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${group.items.length} ${
                            group.items.length === 1 ? "empresa" : "empresas"
                          }`}
                          sx={{
                            ...pageKpiChipSx,
                            bgcolor: alpha(groupAccent, 0.14),
                            color: groupAccent,
                            border: `1px solid ${alpha(groupAccent, 0.28)}`,
                          }}
                        />
                        <Box
                          sx={{
                            flex: 1,
                            height: 1,
                            bgcolor: alpha(theme.palette.divider, 0.8),
                          }}
                        />
                      </Stack>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(auto-fill, minmax(280px, 1fr))",
                          },
                          gap: 1.25,
                        }}
                      >
                        {group.items.map((card) => {
                          const accent =
                            card.company?.brandColor ||
                            theme.palette.primary.main;
                          const cities = companyCities(card.company);
                          return (
                            <Box
                              key={card.id}
                              sx={{
                                ...brandCardSx(theme, accent),
                                p: 1.75,
                                pl: 2.1,
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={1.2}
                                alignItems="flex-start"
                              >
                                <LogoBadge company={card.company} size={40} />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography
                                    fontWeight={800}
                                    sx={{ lineHeight: 1.2 }}
                                  >
                                    {card.company?.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    /{card.cardSlug || card.company?.slug}
                                  </Typography>
                                </Box>
                              </Stack>
                              <Stack
                                direction="row"
                                spacing={0.6}
                                mt={1.3}
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ flex: 1 }}
                              >
                                <Chip
                                  size="small"
                                  label={card.active ? "Activa" : "Oculta"}
                                  sx={brandChipSx(
                                    card.active ? accent : "#78909c",
                                  )}
                                />
                                {card.walletChinaEnabled ? (
                                  <Chip
                                    size="small"
                                    label="WeChat CN"
                                    sx={brandChipSx("#07c160")}
                                  />
                                ) : null}
                                {cities.map((city) => (
                                  <Chip
                                    key={city}
                                    size="small"
                                    label={city}
                                    sx={brandChipSx(accent)}
                                  />
                                ))}
                              </Stack>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                mt={1.4}
                                flexWrap="wrap"
                                useFlexGap
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  flexWrap="wrap"
                                  useFlexGap
                                >
                                  <Button
                                    size="small"
                                    startIcon={<EditOutlinedIcon />}
                                    sx={listActionSx(theme, { accent })}
                                    onClick={() =>
                                      openCardForm({
                                        ...emptyCard(),
                                        ...card,
                                        companyAddresses:
                                          companyAddressesForForm(
                                            card.company ||
                                              companies.find(
                                                (c) =>
                                                  Number(c.id) ===
                                                  Number(card.companyId),
                                              ),
                                          ),
                                      })
                                    }
                                  >
                                    Editar
                                  </Button>
                                  {card.walletEnabled ? (
                                    <Button
                                      size="small"
                                      startIcon={
                                        <AccountBalanceWalletOutlinedIcon />
                                      }
                                      href={visitCardAssetUrl(card.walletUrl)}
                                      sx={listActionSx(theme, { accent })}
                                    >
                                      {t("visitCards.addToWallet")}
                                    </Button>
                                  ) : null}
                                  {card.walletChinaEnabled ? (
                                    <Button
                                      size="small"
                                      startIcon={
                                        <AccountBalanceWalletOutlinedIcon />
                                      }
                                      href={visitCardAssetUrl(
                                        card.walletChinaUrl,
                                      )}
                                      sx={listActionSx(theme, { accent })}
                                    >
                                      {t("visitCards.addToWalletChina")}
                                    </Button>
                                  ) : null}
                                </Stack>
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={async () => {
                                    await deleteVisitCard(card.id);
                                    await load();
                                  }}
                                >
                                  <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}
        </Box>
      ) : null}

      <Dialog
        open={Boolean(companyForm)}
        onClose={() => setCompanyForm(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {companyForm?.id ? "Editar empresa" : "Nueva empresa"}
        </DialogTitle>
        {companyForm ? (
          <DialogContent>
            <Stack spacing={1.5} mt={1}>
              <TextField
                label="Nombre"
                value={companyForm.name}
                sx={pageFieldSx(theme)}
                onChange={(e) =>
                  setCompanyForm((form) => ({
                    ...form,
                    name: e.target.value,
                    slug: form.id
                      ? form.slug
                      : slugifyVisitCard(e.target.value),
                  }))
                }
              />
              <TextField
                label="Slug URL"
                value={companyForm.slug}
                sx={pageFieldSx(theme)}
                onChange={(e) =>
                  setCompanyForm((form) => ({
                    ...form,
                    slug: slugifyVisitCard(e.target.value),
                  }))
                }
              />
              <TextField
                label="Color"
                value={companyForm.brandColor}
                sx={pageFieldSx(theme)}
                onChange={(e) =>
                  setCompanyForm((form) => ({
                    ...form,
                    brandColor: e.target.value,
                  }))
                }
              />
              <TextField
                select
                label="Logo"
                value={resolvedVisitCardLogoKey(
                  companyForm.logoKey,
                  companyForm,
                )}
                sx={pageFieldSx(theme)}
                onChange={(e) =>
                  setCompanyForm((form) => ({
                    ...form,
                    logoKey: e.target.value,
                  }))
                }
              >
                {Object.entries(VISIT_CARD_LOGOS).map(([key, logo]) => (
                  <MenuItem key={key} value={key}>
                    {logo.label}
                  </MenuItem>
                ))}
              </TextField>
              <Box
                component="img"
                src={
                  VISIT_CARD_LOGOS[
                    resolvedVisitCardLogoKey(companyForm.logoKey, companyForm)
                  ]?.src
                }
                alt=""
                sx={{
                  width: 88,
                  height: 44,
                  objectFit: "contain",
                  bgcolor: "#fff",
                  borderRadius: 1,
                  p: 0.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              />
              <TextField
                label="Web"
                value={companyForm.website}
                sx={pageFieldSx(theme)}
                onChange={(e) =>
                  setCompanyForm((form) => ({
                    ...form,
                    website: e.target.value,
                  }))
                }
              />
              <Divider />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                gap={1}
              >
                <Box>
                  <Typography fontWeight={800}>Ciudades</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Una ficha por ciudad. Puedes añadir Sitges, Shanghai,
                    Guangzhou…
                  </Typography>
                </Box>
                <Button
                  startIcon={<AddOutlinedIcon />}
                  onClick={() =>
                    setCompanyForm((form) => ({
                      ...form,
                      addresses: [
                        ...(form.addresses || []),
                        emptyAddress({
                          country: form.addresses?.[0]?.country || "España",
                        }),
                      ],
                    }))
                  }
                >
                  Añadir ciudad
                </Button>
              </Stack>
              {(companyForm.addresses || []).map((addr, idx) => (
                <Box
                  key={addr.id || idx}
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={1}
                    >
                      <Typography fontWeight={800}>
                        {addr.city || `Ciudad ${idx + 1}`}
                      </Typography>
                      <Button
                        color="error"
                        size="small"
                        onClick={() =>
                          setCompanyForm((form) => ({
                            ...form,
                            addresses: form.addresses.filter(
                              (_, i) => i !== idx,
                            ),
                          }))
                        }
                      >
                        Quitar
                      </Button>
                    </Stack>
                    <TextField
                      label="Ciudad"
                      value={addr.city || ""}
                      sx={pageFieldSx(theme)}
                      onChange={(e) => {
                        const city = e.target.value;
                        setCompanyForm((form) => {
                          const prev = form.addresses[idx] || {};
                          const patch = { city };
                          if (!prev.label || prev.label === prev.city) {
                            patch.label = city;
                          }
                          return patchAddress(form, idx, patch);
                        });
                      }}
                    />
                    <TextField
                      label="Calle"
                      value={addr.street || ""}
                      sx={pageFieldSx(theme)}
                      onChange={(e) =>
                        setCompanyForm((form) =>
                          patchAddress(form, idx, { street: e.target.value }),
                        )
                      }
                    />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <TextField
                        label="CP"
                        value={addr.zip || ""}
                        sx={pageFieldSx(theme)}
                        onChange={(e) =>
                          setCompanyForm((form) =>
                            patchAddress(form, idx, { zip: e.target.value }),
                          )
                        }
                      />
                      <TextField
                        fullWidth
                        label="Región / provincia"
                        value={addr.region || ""}
                        sx={pageFieldSx(theme)}
                        onChange={(e) =>
                          setCompanyForm((form) =>
                            patchAddress(form, idx, { region: e.target.value }),
                          )
                        }
                      />
                      <TextField
                        fullWidth
                        label="País"
                        value={addr.country || ""}
                        sx={pageFieldSx(theme)}
                        onChange={(e) =>
                          setCompanyForm((form) =>
                            patchAddress(form, idx, {
                              country: e.target.value,
                            }),
                          )
                        }
                      />
                    </Stack>
                    <TextField
                      label="Etiqueta (sede, showroom…)"
                      value={addr.label || ""}
                      sx={pageFieldSx(theme)}
                      onChange={(e) =>
                        setCompanyForm((form) =>
                          patchAddress(form, idx, { label: e.target.value }),
                        )
                      }
                    />
                    <TextField
                      label="Google Maps"
                      value={addr.mapsUrl || ""}
                      sx={pageFieldSx(theme)}
                      onChange={(e) =>
                        setCompanyForm((form) =>
                          patchAddress(form, idx, { mapsUrl: e.target.value }),
                        )
                      }
                    />
                    <TextField
                      label="Responsable (opcional)"
                      value={addr.responsible || ""}
                      sx={pageFieldSx(theme)}
                      helperText="Se muestra debajo de la dirección en la tarjeta pública"
                      onChange={(e) =>
                        setCompanyForm((form) =>
                          patchAddress(form, idx, {
                            responsible: e.target.value,
                          }),
                        )
                      }
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </DialogContent>
        ) : null}
        <DialogActions>
          {companyForm?.id ? (
            <Button
              color="error"
              onClick={async () => {
                try {
                  await deleteVisitCardCompany(companyForm.id);
                  setCompanyForm(null);
                  await load();
                } catch (err) {
                  setError(err?.response?.data?.error || "No se pudo borrar");
                }
              }}
            >
              Borrar
            </Button>
          ) : null}
          <Button onClick={() => setCompanyForm(null)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={persistCompany}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(cardForm)}
        onClose={() => setCardForm(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.8),
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 24px 64px rgba(0,0,0,0.5)"
                : "0 24px 64px rgba(15,23,42,0.14)",
          },
        }}
      >
        <DialogTitle
          sx={{
            ...pageHeroShellSx(theme),
            mb: 0,
            borderRadius: 0,
            px: 2.5,
            py: 2,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: "1.2rem" }}>
            {cardForm?.id ? "Editar tarjeta" : "Emitir tarjeta"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
            Datos visibles en el enlace público y en el QR.
          </Typography>
        </DialogTitle>
        {cardForm ? (
          <DialogContent sx={{ px: 2.5, pb: 1, pt: 2.2 }}>
            <Stack spacing={2}>
              <Typography sx={pageSectionTitleSx}>Identidad</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  select
                  fullWidth
                  label="Persona"
                  value={cardForm.userId}
                  sx={pageFieldSx(theme)}
                  onChange={async (e) => {
                    const user = people.find(
                      (u) => String(u.id) === String(e.target.value),
                    );
                    const nextSlug = slugifyVisitCard(
                      user?.username || cardForm.userSlug,
                    );
                    const next = {
                      ...cardForm,
                      userId: Number(e.target.value),
                      userSlug: nextSlug,
                      fullName: cardForm.fullName || user?.username || "",
                      email: cardForm.email || user?.email || user?.EMAIL || "",
                    };
                    setCardForm(await hydrateWechatChina(nextSlug, next));
                  }}
                >
                  {people.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.username} · {user.email || user.EMAIL || ""}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="Empresa"
                  value={cardForm.companyId}
                  sx={pageFieldSx(theme)}
                  onChange={(e) => {
                    const company = companies.find(
                      (c) => String(c.id) === String(e.target.value),
                    );
                    setCardForm((form) => ({
                      ...form,
                      companyId: Number(e.target.value),
                      cardSlug: company?.slug || form.cardSlug,
                      companyAddresses: companyAddressesForForm(company),
                    }));
                  }}
                >
                  {companiesSorted.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  fullWidth
                  label="Nombre visible"
                  value={cardForm.fullName}
                  sx={pageFieldSx(theme)}
                  onChange={(e) =>
                    setCardForm((form) => ({
                      ...form,
                      fullName: e.target.value,
                    }))
                  }
                />
                <TextField
                  fullWidth
                  label="Cargo"
                  value={cardForm.roleTitle}
                  sx={pageFieldSx(theme)}
                  onChange={(e) =>
                    setCardForm((form) => ({
                      ...form,
                      roleTitle: e.target.value,
                    }))
                  }
                />
              </Stack>
              <TextField
                select
                label="Estado"
                value={cardForm.active ? "1" : "0"}
                sx={{ ...pageFieldSx(theme), maxWidth: { sm: 220 } }}
                onChange={(e) =>
                  setCardForm((form) => ({
                    ...form,
                    active: e.target.value === "1",
                  }))
                }
              >
                <MenuItem value="1">Activa</MenuItem>
                <MenuItem value="0">Oculta</MenuItem>
              </TextField>

              <Typography sx={pageSectionTitleSx}>Enlace público</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  fullWidth
                  label="URL usuario /c/"
                  value={cardForm.userSlug}
                  sx={pageFieldSx(theme)}
                  onChange={(e) =>
                    setCardForm((form) => ({
                      ...form,
                      userSlug: slugifyVisitCard(e.target.value),
                    }))
                  }
                />
                <TextField
                  fullWidth
                  label="URL empresa"
                  value={cardForm.cardSlug}
                  sx={pageFieldSx(theme)}
                  onChange={(e) =>
                    setCardForm((form) => ({
                      ...form,
                      cardSlug: slugifyVisitCard(e.target.value),
                    }))
                  }
                />
              </Stack>

              <Typography sx={pageSectionTitleSx}>Contacto</Typography>
              <TextField
                label="Email"
                value={cardForm.email}
                sx={pageFieldSx(theme)}
                onChange={(e) =>
                  setCardForm((form) => ({ ...form, email: e.target.value }))
                }
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  value={cardForm.phone}
                  sx={pageFieldSx(theme)}
                  onChange={(e) =>
                    setCardForm((form) => ({ ...form, phone: e.target.value }))
                  }
                />
                <TextField
                  fullWidth
                  label="Móvil"
                  value={cardForm.mobile}
                  sx={pageFieldSx(theme)}
                  onChange={(e) =>
                    setCardForm((form) => ({ ...form, mobile: e.target.value }))
                  }
                />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  fullWidth
                  label="WhatsApp"
                  value={cardForm.whatsapp}
                  sx={pageFieldSx(theme)}
                  onChange={(e) =>
                    setCardForm((form) => ({
                      ...form,
                      whatsapp: e.target.value,
                    }))
                  }
                />
                <TextField
                  fullWidth
                  label="WeChat"
                  value={cardForm.wechat}
                  sx={pageFieldSx(theme)}
                  onChange={(e) =>
                    setCardForm((form) => ({ ...form, wechat: e.target.value }))
                  }
                />
              </Stack>

              <Typography sx={pageSectionTitleSx}>WeChat China (QR)</Typography>
              <Typography variant="body2" color="text.secondary">
                Sube el QR oficial de WeChat de esta persona. Sirve para el pase
                Wallet China. El ID de arriba no basta: hace falta la imagen del
                código.
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                {cardForm.wechatChinaImagePreview ||
                cardForm.wechatChinaImageBase64 ? (
                  <Box
                    component="img"
                    src={
                      cardForm.wechatChinaImageBase64 ||
                      cardForm.wechatChinaImagePreview
                    }
                    alt="QR WeChat China"
                    sx={{
                      width: 120,
                      height: 120,
                      objectFit: "contain",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "#fff",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: 2,
                      border: "1px dashed",
                      borderColor: "divider",
                      display: "grid",
                      placeItems: "center",
                      color: "text.secondary",
                      px: 1,
                      textAlign: "center",
                      fontSize: "0.75rem",
                    }}
                  >
                    Sin QR China
                  </Box>
                )}
                <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<QrCode2OutlinedIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 2,
                      alignSelf: "flex-start",
                    }}
                  >
                    {cardForm.wechatChinaImagePreview ||
                    cardForm.wechatChinaImageBase64
                      ? "Cambiar QR WeChat"
                      : "Subir QR WeChat"}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const dataUrl = await fileToBase64(file);
                        setCardForm((form) => ({
                          ...form,
                          wechatChinaImageBase64: dataUrl,
                          wechatChinaImageMime: file.type || "image/jpeg",
                          wechatChinaImagePreview: dataUrl,
                          wechatChinaHasImage: true,
                          clearWechatChina: false,
                        }));
                      }}
                    />
                  </Button>
                  {(cardForm.wechatChinaHasImage ||
                    cardForm.wechatChinaPayload ||
                    cardForm.wechatChinaImageBase64) && (
                    <Button
                      size="small"
                      color="error"
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        alignSelf: "flex-start",
                      }}
                      onClick={() =>
                        setCardForm((form) => ({
                          ...form,
                          wechatChinaPayload: "",
                          wechatChinaImageBase64: "",
                          wechatChinaImagePreview: "",
                          wechatChinaHasImage: false,
                          clearWechatChina: true,
                        }))
                      }
                    >
                      Quitar QR China
                    </Button>
                  )}
                  <TextField
                    fullWidth
                    label="Enlace del QR (u.wechat.com/...)"
                    value={cardForm.wechatChinaPayload || ""}
                    sx={pageFieldSx(theme)}
                    helperText="Si al guardar no se lee solo, pega aquí el enlace del QR."
                    onChange={(e) =>
                      setCardForm((form) => ({
                        ...form,
                        wechatChinaPayload: e.target.value,
                        clearWechatChina: false,
                      }))
                    }
                  />
                </Stack>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  fullWidth
                  label="LinkedIn"
                  value={cardForm.linkedin}
                  sx={pageFieldSx(theme)}
                  onChange={(e) =>
                    setCardForm((form) => ({
                      ...form,
                      linkedin: e.target.value,
                    }))
                  }
                />
                <TextField
                  fullWidth
                  label="Web"
                  value={cardForm.website}
                  sx={pageFieldSx(theme)}
                  onChange={(e) =>
                    setCardForm((form) => ({
                      ...form,
                      website: e.target.value,
                    }))
                  }
                />
              </Stack>
              <TextField
                label="Bio"
                value={cardForm.bio}
                multiline
                minRows={2}
                sx={pageFieldSx(theme)}
                onChange={(e) =>
                  setCardForm((form) => ({ ...form, bio: e.target.value }))
                }
              />

              {(cardForm.companyAddresses || []).length ? (
                <>
                  <Typography sx={pageSectionTitleSx}>
                    Responsable por ciudad
                  </Typography>
                  {(cardForm.companyAddresses || []).map((addr, idx) => (
                    <TextField
                      key={addr.id || `${addr.city}-${idx}`}
                      label={`Responsable · ${addr.city || addr.label || `Sede ${idx + 1}`}`}
                      value={addr.responsible || ""}
                      sx={pageFieldSx(theme)}
                      helperText="Se muestra debajo de la dirección en la tarjeta pública"
                      onChange={(e) =>
                        setCardForm((form) => {
                          const companyAddresses = [
                            ...(form.companyAddresses || []),
                          ];
                          companyAddresses[idx] = {
                            ...companyAddresses[idx],
                            responsible: e.target.value,
                          };
                          return { ...form, companyAddresses };
                        })
                      }
                    />
                  ))}
                </>
              ) : null}

              <Typography sx={pageSectionTitleSx}>Foto</Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {cardForm.photoBase64 || cardForm.photoUrl ? (
                  <Box
                    component="img"
                    src={
                      cardForm.photoBase64 ||
                      visitCardAssetUrl(cardForm.photoUrl)
                    }
                    alt=""
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid",
                      borderColor: "divider",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: "grid",
                      placeItems: "center",
                      color: "primary.main",
                    }}
                  >
                    <PhotoCameraOutlinedIcon />
                  </Box>
                )}
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<PhotoCameraOutlinedIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                >
                  {cardForm.photoBase64 || cardForm.photoUrl
                    ? "Cambiar foto"
                    : "Subir foto"}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (!file) return;
                      const dataUrl = await fileToBase64(file);
                      const photo = normalizeVisitCardPhoto(
                        dataUrl,
                        file.type || "image/jpeg",
                      );
                      setCardForm((form) => ({
                        ...form,
                        photoBase64: photo.photoBase64,
                        photoMime: photo.photoMime,
                        clearPhoto: false,
                      }));
                    }}
                  />
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        ) : null}
        <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
          <Button
            onClick={() => setCardForm(null)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={persistCard}
            sx={pagePrimaryButtonSx(theme)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(ok)}
        autoHideDuration={2500}
        onClose={() => setOk("")}
      >
        <Alert severity="success" onClose={() => setOk("")}>
          {ok}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={4000}
        onClose={() => setError("")}
      >
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VisitCardsAdminPage;
